import { Injectable, signal } from "@angular/core";

/**
 * Describes what the user is currently looking at, so the copilot can answer
 * "this job" / "this iteration" style questions without the user repeating IDs.
 * Pages (e.g. the model-new viewer) push context here; the copilot reads it.
 */
export interface CopilotPageContext {
  projectId?: string;
  projectName?: string;
  jobId?: string;
  jobName?: string;
  iteration?: number;
  model?: string;
}

/**
 * A one-click suggested prompt surfaced in the copilot, tailored to the page.
 */
export interface CopilotQuickPrompt {
  label: string;
  prompt: string;
}

@Injectable({ providedIn: "root" })
export class CopilotContextService {
  /** Current page context, or null when nothing relevant is open. */
  readonly context = signal<CopilotPageContext | null>(null);

  /** Page-specific quick prompts shown above the input. */
  readonly quickPrompts = signal<CopilotQuickPrompt[]>([]);

  setContext(ctx: CopilotPageContext | null) {
    this.context.set(ctx);
  }

  clearContext() {
    this.context.set(null);
    this.quickPrompts.set([]);
  }

  setQuickPrompts(prompts: CopilotQuickPrompt[]) {
    this.quickPrompts.set(prompts ?? []);
  }

  /**
   * Builds a compact context preamble that is prepended to the user's question
   * so the agent resolves "this job"/"this iteration" deterministically.
   * Returns "" when there is no useful context.
   */
  buildPreamble(): string {
    const ctx = this.context();
    if (!ctx) return "";
    const parts: string[] = [];
    if (ctx.jobName) {
      parts.push(`job "${ctx.jobName}"${ctx.jobId ? ` (id ${ctx.jobId})` : ""}`);
    }
    if (ctx.projectName) {
      parts.push(`project "${ctx.projectName}"${ctx.projectId ? ` (id ${ctx.projectId})` : ""}`);
    }
    if (ctx.iteration !== undefined && ctx.iteration !== null) {
      parts.push(`iteration ${ctx.iteration}`);
    }
    if (ctx.model) {
      parts.push(`model "${ctx.model}"`);
    }
    if (parts.length === 0) return "";
    return `[Context: the user is currently viewing ${parts.join(", ")}. ` +
      `If they refer to "this job", "this project" or "this iteration", resolve it to the above.]\n\n`;
  }
}
